import { IGatsbyImageData } from "gatsby-plugin-image";
import styled from "styled-components";
import ScrollableOverflow from "./ScrollableOverflow";
import { useCallback, useState } from "react";
import useThrottledResizeObserver from "../hooks/useThrottledResizeObserver";
import { ResizeHandler } from "use-resize-observer";
import ExpertCard from "./ExpertCard";

const ExpertCarouselWrapper = styled.div`
  position: relative;
`;

const ExpertCarouselHeadline = styled.h2`
  margin-bottom: var(--spacing-0_75);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-1);
  }
`;

const ExpertCarouselSubhead = styled.p`
  margin-bottom: var(--spacing-2);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-3);
    max-width: 66.66%;
  }
`;

const ExpertCardWrapper = styled.div`
  flex-direction: column;
  width: 213px;
  margin-right: var(--spacing-1);

  &:last-child {
    margin-right: 0;
  }

  @media (min-width: 933px) {
    width: 300px;
    margin-right: var(--spacing-1_5);
  }
`;

type PropTypes = {
  headline?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  subhead?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  experts?: {
    contentful_id: string;
    name: string;
    title: string;
    image: { title: string; gatsbyImageData: IGatsbyImageData };
  }[];
};

const ExpertsCarousel = ({ headline, subhead, experts }: PropTypes) => {
  const [cardWidth, setCardWidth] = useState(213);

  const calculateCardWidth = useCallback<ResizeHandler>(
    ({ width: resizedWrapperWidth }) => {
      const desktop =
        window.matchMedia && window.matchMedia("(min-width: 768px)").matches;

      if (!desktop) {
        const wrapperWidth = resizedWrapperWidth || 213;
        const gutter = 16 * 1.5;
        setCardWidth((wrapperWidth - gutter) * 0.66);
      } else {
        const wrapperWidth = resizedWrapperWidth || 300;
        const gutter = 16 * 1.5 * 3;
        setCardWidth((wrapperWidth - gutter) * 0.25);
      }
    },
    [],
  );

  const { ref: wrapperRef } = useThrottledResizeObserver({
    onResize: calculateCardWidth,
  });

  return (
    <ExpertCarouselWrapper ref={wrapperRef} className="ExpertCarouselWrapper">
      <ExpertCarouselHeadline
        className="typography-headline2"
        dangerouslySetInnerHTML={{
          __html: headline?.childMarkdownRemark?.html || "",
        }}
      />
      <ExpertCarouselSubhead
        className="typography-body1 font-circular"
        dangerouslySetInnerHTML={{
          __html: subhead?.childMarkdownRemark?.html || "",
        }}
      />
      <ScrollableOverflow bleedRight={true} bleedLeft={true}>
        {experts?.map((expert) => {
          return (
            <ExpertCardWrapper
              key={expert.contentful_id}
              style={{ width: cardWidth }}
            >
              <ExpertCard expert={expert} />
            </ExpertCardWrapper>
          );
        })}
      </ScrollableOverflow>
    </ExpertCarouselWrapper>
  );
};

export default ExpertsCarousel;
